import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import moment from 'moment';
import { ActionListDialogComponent, ActionListDialogData, ListType } from './action-list-dialog.component';

import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule, MatSelectionListChange } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterModule } from '@angular/router';
import { AccountGroup } from '@galaxy/account-group';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FeatureFlagService } from '@vendasta/businesses';
import { Statuses } from '@vendasta/campaigns';
import { SendersService, SenderType } from '@vendasta/email';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyInputModule } from '@vendasta/galaxy/input';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';
import { VaSearchSelectModule } from '@vendasta/uikit';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { debounceTime, map, tap } from 'rxjs/operators';
import { AccountSelectorDialogComponent } from '../../accounts/account-selector-dialog/account-selector-dialog.component';
import { DatetimePickerModule } from '../../common/va-datetime-picker';
import { FeatureFlags } from '../../core/features';
import { PartnerService } from '../../core/partner.service';
import { CampaignSettingsService } from '../../email-settings/campaign-settings.service';
import { CampaignsService, DEFAULT_SEND_DELAY } from '../../marketing/marketing-automation-page/campaigns.service';
import { ActionList } from '../action-list';
import { ActionsApiService } from './actions.api.service';
import { ViewActionListHistoryDialogComponent } from './view-action-list-history-dialog.component';

enum SendFromEmail {
  assigned,
  default,
  custom,
}

interface SelectedCampaign {
  id: string;
  name: string;
}

@Component({
  templateUrl: './add-to-campaign-dialog.component.html',
  styleUrls: [
    './action-list-dialog.component.scss',
    './action-list-actions.component.scss',
    './add-to-campaign-dialog.component.scss',
  ],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    GalaxyFormFieldModule,
    GalaxyInputModule,
    GalaxyPipesModule,
    MatListModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatOptionModule,
    MatSelectModule,
    VaSearchSelectModule,
    RouterModule,
    MatIconModule,
    DatetimePickerModule,
    MatCardModule,
    GalaxyLoadingSpinnerModule,
    MatInputModule,
    MatCheckboxModule,
    GalaxyButtonLoadingIndicatorModule,
  ],
})
export class AddToCampaignDialogComponent extends ActionListDialogComponent implements OnInit, OnDestroy {
  DateFormat = DateFormat;
  subscriptions: Subscription[] = [];
  campaigns: SelectedCampaign[];

  private readonly campaignSearchTerm$$ = new BehaviorSubject<string>('');
  selectedCampaign: SelectedCampaign;
  selectedCampaignDelay = 0;
  selectedCampaignTimezone = 'UTC';
  campaignsLoading = true;
  buttonLoading = false;
  campaignStartLoading = false;
  showScheduledStartPicker: boolean;
  defaultAccountGroup: AccountGroup;
  scheduledStart: Date;
  validDate = true;
  isConfirmed = false;
  showInfo = false;
  showSnapshotConsent$: Observable<boolean>;
  lt: typeof ListType = ListType;
  selectedActionList: ActionList;
  defaultEmailAddress: string;
  showSendFrom$: Observable<boolean>;
  sendFromForm: UntypedFormGroup;
  sendFrom: typeof SendFromEmail = SendFromEmail;
  sendFromDescription: string;
  selectedSendFrom: any;
  selectedSendFromText$$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  selectedSendFromText$: Observable<string> = this.selectedSendFromText$$.asObservable();
  defaultName: string;
  useCustomEmail = false;
  senderInfoDomain$: Observable<string>;
  senderInfoDomain: string;

  constructor(
    public dialogRef: MatDialogRef<AddToCampaignDialogComponent>,
    public dialog: MatDialog,
    public router: Router,
    @Inject(MAT_DIALOG_DATA) private readonly data: ActionListDialogData,
    private readonly campaignsService: CampaignsService,
    private actionsService: ActionsApiService,
    private partner: PartnerService,
    private snackbarService: SnackbarService,
    private campaignSettingsService: CampaignSettingsService,
    private translateService: TranslateService,
    private feature: FeatureFlagService,
    private readonly senderAPI: SendersService,
  ) {
    super(dialogRef, router);
  }

  ngOnInit(): void {
    this.showSendFrom$ = this.feature.checkFeatureFlag(
      this.partner.partnerId,
      '',
      FeatureFlags.CUSTOMIZE_CAMPAIGN_SEND_FROM,
    );
    this.senderInfoDomain$ = this.senderAPI
      .getEmailSendingInfo(SenderType.SENDER_TYPE_PARTNER, this.partner.partnerId)
      .pipe(map((r) => r.domain));
    this.sendFromForm = new UntypedFormGroup({
      sendFromSelector: new UntypedFormControl(''),
      sendFromEmail: new UntypedFormControl('', [Validators.required]),
      sendFromName: new UntypedFormControl('', [Validators.required]),
    });
    this.setDefaultSelectedText();

    this.subscriptions.push(
      this.campaignSearchTerm$$
        .pipe(
          debounceTime(1000), //
        )
        .subscribe((t) => this.loadCampaignsList(t, false)),
    );

    this.campaignSettingsService.getSendAsInformation(this.partner.partnerId);
    this.subscriptions.push(
      this.campaignSettingsService.getSendAsInformation(this.partner.partnerId).subscribe((sendAsInformation) => {
        this.defaultEmailAddress = sendAsInformation.sendAsEmailAddress;
        this.defaultName = sendAsInformation.sendAsName;
        this.sendFromDescription = this.translateService.instant(
          'CAMPAIGN_DETAILS.CONFIGURATION.SEND_FROM.ASSIGNED_SALESPERSON_DESCRIPTION',
          { defaultEmail: sendAsInformation.sendAsEmailAddress },
        );
      }),
      this.senderInfoDomain$.subscribe((domain) => {
        this.senderInfoDomain = domain;
      }),
    );
  }

  private loadCampaignsList(searchTerm: string, debounce = true): void {
    const dTime = debounce ? 1000 : 0;
    this.campaignsLoading = true;
    this.campaignsService
      .listCampaigns(
        this.partner.partnerId, //
        undefined,
        [Statuses.STATUSES_PUBLISHED, Statuses.STATUSES_ACTIVE],
        searchTerm,
        undefined,
      )
      .pipe(
        debounceTime(dTime), //
      )
      .subscribe((campaigns) => {
        this.campaignsLoading = false;
        this.campaigns = campaigns.map((v) => ({
          id: v.campaignId,
          name: v.name,
        }));
      });
  }

  addToCampaign(): void {
    this.sendFromForm.markAllAsTouched();

    if (this.selectedSendFrom === SendFromEmail.custom && !this.sendFromForm.valid) {
      return;
    }

    if (
      !(this.selectedCampaign || this.selectedActionList) ||
      this.buttonLoading ||
      (this.scheduledStart && !this.validDate)
    ) {
      return;
    }
    this.buttonLoading = true;

    let response$: Observable<any>;
    let fromName: string;
    let fromEmail: string;
    if (this.selectedSendFrom === SendFromEmail.assigned) {
      fromName = null;
      fromEmail = null;
    } else if (this.selectedSendFrom === SendFromEmail.default) {
      fromName = this.defaultName;
      fromEmail = this.defaultEmailAddress;
    } else if (this.selectedSendFrom === SendFromEmail.custom) {
      fromName = this.sendFromForm.controls.sendFromName.value;
      fromEmail = this.sendFromForm.controls.sendFromEmail.value + '@' + this.senderInfoDomain;
    }

    if (!fromName || !fromEmail) {
      fromName = null;
      fromEmail = null;
    }

    if (this.listType === ListType.CampaignList) {
      if (this.data && this.data.useBulkActionsMicroservice) {
        response$ = this.actionsService.addListToCampaignBulkAction(
          this.campaignId,
          this.selectedActionList.id,
          this.campaignStart,
          '',
          fromName,
          fromEmail,
        );
      } else {
        response$ = this.actionsService.addListToCampaign(
          this.campaignId,
          this.selectedActionList.id,
          this.campaignStart,
          fromName,
          fromEmail,
        );
      }
    } else if (this.listType === ListType.AccountList) {
      if (this.data && this.data.useBulkActionsMicroservice) {
        response$ = this.actionsService.addListToCampaignBulkAction(
          this.selectedCampaign.id,
          this.actionList.id,
          this.campaignStart,
          '',
          fromName,
          fromEmail,
        );
      } else {
        response$ = this.actionsService.addListToCampaign(
          this.selectedCampaign.id,
          this.actionList.id,
          this.campaignStart,
          fromName,
          fromEmail,
        );
      }
    } else if (this.listType === ListType.UserList) {
      this.buttonLoading = false;
      this.useCustomEmail = this.selectedSendFrom === SendFromEmail.custom;
      response$ = this.openAccountDefaultDialogue(fromEmail, fromName, this.useCustomEmail);
    }

    response$.subscribe(
      (response) => {
        if (!response && this.listType === ListType.UserList) {
          return;
        }
        this.buttonLoading = false;
        if (this.listType !== ListType.CampaignList) {
          this.snackbarService.successSnack(`Added ${this.listName} to ${this.selectedCampaign.name}.`);
        }
        this.dialogRef.close(true);
        if (this.listType === ListType.CampaignList) {
          const dialogRef = this.dialog.open(ViewActionListHistoryDialogComponent, {
            width: '660px',
          });
          dialogRef.componentInstance.campaignName = this.campaignName;
          dialogRef.componentInstance.actionListName = this.selectedActionList.name;
          dialogRef.componentInstance.actionListId = this.selectedActionList.id;
        }
      },
      (err) => {
        this.buttonLoading = false;
        if (err.code === 400) {
          const duration = 8000;
          const hasAccessToAllMarkets = this.partner.MarketAccess.length === 0;
          const canCustomizeWhiteLabel = this.partner.CanCustomizeWhitelabel;
          if (hasAccessToAllMarkets && canCustomizeWhiteLabel) {
            const snackBarRef = this.snackbarService.errorSnack(err.data.message, 'Edit Address', duration);
            snackBarRef.onAction().subscribe(() => {
              window.location.href = '/customize-design/?open=email-settings-report';
            });
          } else {
            this.snackbarService.errorSnack(err.data.message, null, duration);
          }
        } else {
          this.snackbarService.errorSnack(
            `Failed to add ${this.listName} to ${this.selectedCampaign.name}. Please try again.`,
          );
        }
      },
    );
  }

  get campaignStart(): Date {
    let time;
    if (this.scheduledStart) {
      time = moment(this.scheduledStart);
    } else {
      const localOffset = moment().utcOffset(); // local timezone offset from UTC
      const campaignOffset = moment().tz(this.selectedCampaignTimezone).utcOffset(); // campaign timezone offset from UTC
      time = moment().add(this.selectedCampaignDelay, 's'); // add the campaign delay, usually 5 min
      time = time.subtract(localOffset, 'm'); // set the current time to UTC by subtracting the local offset from it
      time = time.add(campaignOffset, 'm'); // set the current time to the campaign local time by going forward
    }
    return time.toDate();
  }

  startCampaignButtonDisabled(): boolean {
    if ((!this.selectedCampaign && !this.selectedActionList) || !this.isConfirmed) {
      return true;
    }
    return this.scheduledStart && !this.validDate;
  }

  cancelCampaignSelection(): void {
    this.selectedCampaign = undefined;
    this.isConfirmed = false;
    this.showSnapshotConsent$ = of(false);
  }

  campaignSelected(campaign: SelectedCampaign): void {
    this.selectedCampaign = campaign;
    this.campaignStartLoading = true;
    this.campaignsService.getConfig(this.selectedCampaign.id).subscribe((result) => {
      this.campaignStartLoading = false;
      this.selectedCampaignDelay = DEFAULT_SEND_DELAY;
      this.selectedCampaignTimezone = result.timezone;
    });

    this.showSnapshotConsent$ = this.campaignsService.hasSnapshotStep(this.selectedCampaign.id).pipe(
      map((result) => (this.itemCount < 100 ? false : result)),
      tap((result) => (this.isConfirmed = !result)),
    );
    this.showInfo = false;
  }

  onScheduleCampaignClick(): void {
    if (!this.showScheduledStartPicker) {
      // turning on, so set initial value of scheduledStart
      this.scheduledStart = this.campaignStart;
    } else {
      // turning off, so clear scheduled start time
      this.scheduledStart = undefined;
    }
    this.showScheduledStartPicker = !this.showScheduledStartPicker;
  }

  onScheduledStartChange($event: Date): void {
    this.scheduledStart = $event;
  }

  openAccountDefaultDialogue(fromEmail: string, fromName: string, useCustomEmail: boolean): Observable<any> {
    const dialogRef = this.dialog.open(AccountSelectorDialogComponent, {
      width: '660px',
    });
    dialogRef.componentInstance.partnerId = this.partner.partnerId;
    dialogRef.componentInstance.userListId = this.userList.listId;
    dialogRef.componentInstance.userListName = this.listName;
    dialogRef.componentInstance.selectedCampaignId = this.selectedCampaign.id;
    dialogRef.componentInstance.campaignStart = this.campaignStart;
    dialogRef.componentInstance.selectedCampaignTimezome = this.selectedCampaignTimezone;
    dialogRef.componentInstance.fromEmail = fromEmail;
    dialogRef.componentInstance.fromName = fromName;
    dialogRef.componentInstance.useCustomEmail = useCustomEmail;
    return dialogRef.afterClosed().pipe(map((x) => x));
  }

  actionListSelected(actionList: ActionList): void {
    this.selectedActionList = actionList;
    this.campaignStartLoading = true;
    this.campaignsService.getConfig(this.campaignId).subscribe((result) => {
      this.campaignStartLoading = false;
      this.selectedCampaignDelay = DEFAULT_SEND_DELAY;
      this.selectedCampaignTimezone = result.timezone;
    });

    this.showSnapshotConsent$ = this.campaignsService.hasSnapshotStep(this.campaignId).pipe(
      map((result) => (this.itemCount < 100 ? false : result)),
      tap((result) => (this.isConfirmed = !result)),
    );
    this.showInfo = false;
  }

  setDefaultSelectedText(): void {
    if (this.listType !== ListType.UserList) {
      this.selectedSendFromText$$.next(
        this.translateService.instant('CAMPAIGN_DETAILS.CONFIGURATION.SEND_FROM.ASSIGNED_SALESPERSON'),
      );
      this.selectedSendFrom = SendFromEmail.assigned;
      this.sendFromForm.controls.sendFromSelector.setValue(SendFromEmail.assigned);
    } else {
      this.selectedSendFromText$$.next(
        this.translateService.instant('CAMPAIGN_DETAILS.CONFIGURATION.SEND_FROM.DEFAULT_EMAIL'),
      );
      this.selectedSendFrom = SendFromEmail.default;
      this.sendFromForm.controls.sendFromSelector.setValue(SendFromEmail.default);
    }
  }

  updateSendFromSelection(event: any): void {
    switch (event.value) {
      case 0:
        this.selectedSendFromText$$.next(
          this.translateService.instant('CAMPAIGN_DETAILS.CONFIGURATION.SEND_FROM.ASSIGNED_SALESPERSON'),
        );
        break;
      case 1:
        this.selectedSendFromText$$.next(
          this.translateService.instant('CAMPAIGN_DETAILS.CONFIGURATION.SEND_FROM.DEFAULT_EMAIL'),
        );
        break;
      case 2:
        this.selectedSendFromText$$.next(
          this.translateService.instant('CAMPAIGN_DETAILS.CONFIGURATION.SEND_FROM.CUSTOM_EMAIL'),
        );
        break;
    }
    this.sendFromForm.controls.sendFromName.setValue('');
    this.sendFromForm.controls.sendFromEmail.setValue('');
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  startCampaignText(): string {
    if (this.listType === ListType.UserList) {
      return this.translateService.instant('COMMON.ACTION_LABELS.NEXT');
    }
    return this.translateService.instant('LISTS.CAMPAIGN_DIALOG.SUBMIT');
  }

  async handleCampaignSearchChange($event: string): Promise<void> {
    this.campaignSearchTerm$$.next($event);
  }

  handleCampaignSelectionChange($event: MatSelectionListChange): void {
    const selectedCampaigns = $event.options.filter((v) => v.selected);
    if (selectedCampaigns?.length === 0) {
      this.selectedCampaign = undefined;
      return;
    }
    if (selectedCampaigns?.length > 1) {
      console.warn('Multiple selection is not supported');
    }
    this.campaignSelected(selectedCampaigns[0].value);
  }
}
